import type { StructuredContent, StructuredContentDocument } from './types';

export const isStructuredContent = (section: unknown): section is StructuredContentDocument => {
  return (
    section !== null &&
    typeof section !== 'undefined' &&
    typeof section === 'object' &&
    'type' in section &&
    'version' in section &&
    'props' in section
  );
};

export const isStructuredContentType = <T = Record<string, unknown>>(
  value: unknown,
  type: string,
): value is StructuredContentDocument<T> => {
  return isStructuredContent(value) && value.type === type;
};

export const getAllChildrenStrings = (
  sc: StructuredContentDocument<Record<string, unknown>>,
  prevValue = '',
): string => {
  if (Array.isArray(sc.props.children)) {
    for (const child of sc.props.children) {
      if (typeof child === 'string') {
        prevValue = prevValue.concat(child);
      }
      if (isStructuredContent(child)) {
        prevValue = getAllChildrenStrings(child, prevValue);
      }
    }
  }
  if (isStructuredContent(sc?.props?.children)) {
    prevValue = getAllChildrenStrings(sc?.props?.children, prevValue);
  }
  return prevValue;
};

export type GetItemValue = string | boolean | number | Record<string, unknown>;
/**
 * Walks a structured content document depth-first and collects values returned by `query` for each
 * document node. The query function can return null or undefined to skip the node.
 */
export function getItem<T extends GetItemValue>(
  document: StructuredContentDocument,
  query: (doc: StructuredContentDocument) => T | readonly T[] | null | undefined,
): T[] {
  const results: T[] = [];

  function walk(doc: StructuredContentDocument) {
    const item = query(doc);

    if (item !== undefined && item !== null) {
      const batch: T[] = Array.isArray(item) ? [...item] : [item];
      results.push(...batch);
    }

    const { children } = doc.props;
    if (Array.isArray(children)) {
      for (const child of children) {
        if (isStructuredContent(child)) {
          walk(child);
        }
      }
    } else if (isStructuredContent(children)) {
      walk(children);
    }
  }

  walk(document);
  return results;
}

/**
 * Groups structured content nodes into sections based on level 2 headings (H2).
 *
 * This function takes a `StructuredContentDocument` and wraps consecutive nodes that follow
 * each H2 heading inside a `ContentSection`.
 *
 * ## Example Transformation:
 * Input:
 *   - H2
 *   - Paragraph
 *   - Image
 *   - H2
 *   - Paragraph
 *
 * Output:
 *   - Container (H2, Paragraph, Image)
 *   - Container (H2, Paragraph)
 *
 * @param doc - A structured content document to be transformed.
 * @param headingsToExclude - Optional array of heading strings to exclude (case-insensitive exact match).
 *                            Sections with these headings will be completely omitted from the output.
 * @returns A new document with sections grouped under H2s, each wrapped in a `ContentSection`.
 */
export function groupedByH2ContentAndWrapItWithContainer(
  doc: StructuredContentDocument<Record<string, unknown>>,
  headingsToExclude?: string[],
): StructuredContent {
  // Return early if the document is not well-formed
  if (doc?.type !== 'Document' || !Array.isArray(doc?.props?.children)) return doc;

  const wrappedChildren: StructuredContent[] = [];
  let currentSection;
  let isExcludedSection = false;

  for (const child of doc.props.children) {
    if (!isStructuredContent(child)) {
      wrappedChildren.push(child);
      continue;
    }

    const isH2 =
      child.type === 'Container' &&
      Array.isArray(child.props?.children) &&
      isStructuredContent(child.props.children[0]) &&
      child.props.children[0].type === 'ContentHeading' &&
      child.props.children[0].props?.level === 2;

    if (isH2) {
      // Close out the previous section, if one exists and is not excluded
      if (currentSection && !isExcludedSection) {
        wrappedChildren.push({
          type: 'ContentSection',
          version: '1.0.0',
          props: {
            spacing: { bottom: 3 },
            children: currentSection,
          },
        });
      }

      // Check if this H2 should be excluded (case-insensitive exact match)
      const headingText = getAllChildrenStrings(child).toLowerCase();
      isExcludedSection =
        !!headingsToExclude?.some(excluded => headingText === excluded.toLowerCase());

      // Start a new section (will be skipped if excluded)
      currentSection = isExcludedSection ? undefined : [child];
    } else if (currentSection && !isExcludedSection) {
      // Continue the current section (only if not excluded)
      currentSection.push(child);
    } else if (!currentSection && !isExcludedSection) {
      // No section started and not excluded, just push node directly
      wrappedChildren.push(child);
    }
    // If isExcludedSection is true, we skip this child entirely
  }

  // Push the final section if it exists and is not excluded
  if (currentSection && !isExcludedSection) {
    wrappedChildren.push({
      type: 'ContentSection',
      version: '1.0.0',
      props: {
        spacing: { bottom: 3 },
        children: currentSection,
      },
    });
  }

  return {
    ...doc,
    props: {
      ...doc.props,
      children: wrappedChildren,
    },
  };
}

type ContentHeading = {
  id: string;
  header: string;
};

export const getChildrenHeadings = (
  parentDocument: StructuredContentDocument,
  headingsToExclude?: string[],
) => {
  return getItem<ContentHeading>(parentDocument, doc => {
    if (doc.type === 'ContentHeading' && doc.props.level === 2) {
      const childrenString = getAllChildrenStrings(doc);
      const cleanedChildrenString = childrenString.trim();
      if (cleanedChildrenString === '') {
        return null;
      }

      // Filter out excluded headings (case-insensitive exact match)
      if (headingsToExclude?.length) {
        const lowerCaseHeader = cleanedChildrenString.toLowerCase();
        const isExcluded = headingsToExclude.some(
          excluded => lowerCaseHeader === excluded.toLowerCase(),
        );
        if (isExcluded) {
          return null;
        }
      }

      return {
        id: getHeadingId(cleanedChildrenString),
        header: cleanedChildrenString,
      };
    }
  });
};

export const getHeadingId = (childrenString: string) => {
  return childrenString
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '')
    .toLowerCase();
};

export const getChildHeadingId = (doc: StructuredContentDocument) => {
  const heading = getChildrenHeadings(doc)[0];
  return heading.id;
};
